import { inflate, deflate } from 'pako';

/** Query-string parameter holding the compressed, URL-safe-base64 config blob. */
export const CONFIG_PARAM = 'c';

export interface UrlConfigOptions {
    paramName?: string; // Defaults to CONFIG_PARAM
    fallbackParam?: string; // Default 'config' for uncompressed fallback
}

/**
 * Compresses a configuration object and returns a base64 URL-safe string
 */
export function compressConfig(config: Record<string, any>): string {
    try {
        const jsonString = JSON.stringify(config);
        const compressed = deflate(jsonString);
        return btoa(String.fromCharCode(...compressed))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, '');
    } catch (error) {
        console.error('Error compressing config:', error);
        throw error;
    }
}

/**
 * Decompresses a base64 URL-safe string back to a configuration object
 */
export function decompressConfig(compressedConfig: string): Record<string, any> {
    try {
        // Restore base64 padding and characters
        let base64 = compressedConfig.replace(/-/g, '+').replace(/_/g, '/');

        // Add padding if needed
        while (base64.length % 4) {
            base64 += '=';
        }

        const compressed = new Uint8Array(
            atob(base64)
                .split('')
                .map((char) => char.charCodeAt(0))
        );

        const decompressed = inflate(compressed, { to: 'string' });
        return JSON.parse(decompressed);
    } catch (error) {
        console.error('Error decompressing config:', error);
        throw error;
    }
}

/**
 * Reads configuration from URL parameters
 * Supports both compressed (c) and uncompressed (config) parameters
 * If the parameter exists but is empty (e.g., ?c=), returns special reset indicator
 */
export function readConfigFromUrl(options: UrlConfigOptions = {}): Record<string, any> | null | 'RESET' {
    const { paramName = CONFIG_PARAM, fallbackParam = 'config' } = options;

    try {
        const urlParams = new URLSearchParams(window.location.search);

        // Check if compressed parameter exists
        if (urlParams.has(paramName)) {
            const compressedConfig = urlParams.get(paramName);

            // If parameter exists but is empty, signal reset
            if (compressedConfig === '' || compressedConfig === null) {
                console.log('Found empty compressed config parameter, signaling reset to defaults...');
                return 'RESET';
            }

            // Otherwise attempt to decompress
            console.log('Found compressed config parameter, attempting to decompress...');
            return decompressConfig(compressedConfig);
        }

        // Check if uncompressed parameter exists
        if (urlParams.has(fallbackParam)) {
            const uncompressedConfig = urlParams.get(fallbackParam);

            // If parameter exists but is empty, signal reset
            if (uncompressedConfig === '' || uncompressedConfig === null) {
                console.log('Found empty uncompressed config parameter, signaling reset to defaults...');
                return 'RESET';
            }

            // Otherwise parse JSON
            console.log('Found uncompressed config parameter...');
            return JSON.parse(decodeURIComponent(uncompressedConfig));
        }

        return null;
    } catch (error) {
        console.error('Error reading config from URL:', error);
        return null;
    }
}
/**
 * Applies configuration to a target object, with optional property mapping
 */
export function applyUrlConfig(
    target: Record<string, any>,
    config: Record<string, any>,
    propertyMap?: Record<string, string>,
    skipKeys?: string[]
): boolean {
    let applied = false;
    const defaultSkipKeys = ['identitiesSetup']; // Skip complex objects by default
    const keysToSkip = new Set([...defaultSkipKeys, ...(skipKeys || [])]);

    try {
        for (const [key, value] of Object.entries(config)) {
            // Use mapped property name if available, otherwise use original key
            const targetKey = propertyMap?.[key] || key;

            // Skip keys that should not be directly overwritten
            if (keysToSkip.has(targetKey)) {
                console.log(`Skipping URL config for complex object: ${targetKey}`);
                continue;
            }

            if (targetKey in target && typeof target[targetKey] !== 'undefined') {
                // Type-safe assignment - only apply if types match or can be converted
                const currentValue = target[targetKey];
                const currentType = typeof currentValue;
                const newType = typeof value;

                // If current value is null, accept any primitive (or null) — the original
                // type-equality check would otherwise reject all assignments because
                // typeof null === 'object'.
                if (currentValue === null && (value === null || newType === 'string' || newType === 'number' || newType === 'boolean')) {
                    console.log(`Applying URL config: ${targetKey} = ${value}`);
                    target[targetKey] = value;
                    applied = true;
                    continue;
                }

                // Arrays (e.g. Guard Mode detector lists) are replaced wholesale.
                // Must run before the object-skip branch below, since typeof [] === 'object'.
                if (Array.isArray(value) && (Array.isArray(currentValue) || currentValue === null)) {
                    console.log(`Applying URL config (array): ${targetKey}`);
                    target[targetKey] = value;
                    applied = true;
                    continue;
                }

                // Skip if trying to overwrite an object with another object (could be a store instance)
                if (currentType === 'object' && newType === 'object' && currentValue !== null && value !== null) {
                    console.log(`Skipping URL config for object property: ${targetKey}`);
                    continue;
                }

                if (currentType === newType) {
                    console.log(`Applying URL config: ${targetKey} = ${value}`);
                    target[targetKey] = value;
                    applied = true;
                } else if (currentType === 'number' && newType === 'string') {
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue)) {
                        console.log(`Applying URL config (converted): ${targetKey} = ${numValue}`);
                        target[targetKey] = numValue;
                        applied = true;
                    }
                } else if (currentType === 'boolean' && newType === 'string') {
                    const boolValue = value.toLowerCase() === 'true';
                    console.log(`Applying URL config (converted): ${targetKey} = ${boolValue}`);
                    target[targetKey] = boolValue;
                    applied = true;
                } else {
                    console.warn(`Type mismatch for ${targetKey}: expected ${currentType}, got ${newType}`);
                }
            } else {
                console.warn(`Property ${targetKey} not found in target object`);
            }
        }
    } catch (error) {
        console.error('Error applying URL config:', error);
    }

    return applied;
}

/**
 * Generates a URL with compressed configuration
 */
export function generateConfigUrl(baseUrl: string, config: Record<string, any>, options: UrlConfigOptions = {}): string {
    const { paramName = CONFIG_PARAM } = options;

    try {
        const compressedConfig = compressConfig(config);
        const url = new URL(baseUrl);
        url.searchParams.set(paramName, compressedConfig);
        return url.toString();
    } catch (error) {
        console.error('Error generating config URL:', error);
        return baseUrl;
    }
}
